import { Suspense, useEffect, useState, lazy, useMemo } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";

import { enumRoles as role } from "data/enums/authorization";
import { appPaths } from "data/enums/appPaths";
import { Redirect404, RedirectLoggedInUsers, RequirePermission } from "utilities/routes";
import { selectPasswordResetRequired } from "store/slices/authSlice";
import useApiSnackbar from "hooks/useApiSnackbar";

import SplashScreen from "components/loaders/splashScreen/index";
import RootErrorBoundary from "components/errorBoundary/rootErrorBoundary";

import { persistedModuleVersionMatches, migratePersistedModuleVersion } from 'utilities/module/persistedModuleConfig';

// Lazy elements
const Admission = lazy(() => import("views/admission"));
const Admin = lazy(() => import("views/admin"));
const Affiliate = lazy(() => import("views/affiliate"));
const Advertiser = lazy(() => import("views/advertiser"))
const Verification = lazy(() => import('views/verification'));
// const Promotion = lazy(() => import('views/promotion'));
const Logout = lazy(() => import('views/logout'));

const VersionChangeModal = lazy(() => import('components/modals/versionChangeModal'));
// const AnimatedLayout = lazy(() => import('components/animationComponents/animatedLayout'));

const allAdminRoles = [role.ADMIN, role.MANAGER, role.FINANCE, role.SUPPORT]

const Fallback = ({ setSuspenseMounted }) => {
  useEffect(() => { setSuspenseMounted(true); return () => setSuspenseMounted(false) }, [setSuspenseMounted])
  return '';
};

const App = () => {

  const location = useLocation();
  const [suspenseMounted, setSuspenseMounted] = useState(true)
  const key = useMemo(() => {
    const pathname = location?.pathname || ''
    if (pathname.includes('admin')) return 1
    if (pathname.includes('affiliate')) return 2
    if (pathname.includes('admission')) return 3
    if (pathname.includes('advertiser')) return 4
    return pathname
  }, [location?.pathname])

  useApiSnackbar()

  const history = useHistory()
  const passwordResetRequired = useSelector(selectPasswordResetRequired)
  const passwordResetPending = !!passwordResetRequired
  useEffect(() => {
    if (!passwordResetPending) return
    if (location?.pathname?.startsWith('/admission/forgot')) return
    if (location?.pathname?.startsWith('/admission/recover')) return
    history.replace(appPaths.FORGOT_PASSWORD)
  }, [passwordResetPending, location?.pathname, history])

  useEffect(() => {
    if (!persistedModuleVersionMatches()) {
      migratePersistedModuleVersion()
    }
  }, [])


  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      <RootErrorBoundary >
        <SplashScreen suspenseMounted={suspenseMounted} />

        <Suspense fallback={<Fallback setSuspenseMounted={setSuspenseMounted} />}>

          <VersionChangeModal />

          <Switch location={location} key={key}>
            <Route path="/admin/*" >
              <RequirePermission requiredRoles={allAdminRoles}><Admin /></RequirePermission>
            </Route>

            <Route path="/affiliate/*" >
              <RequirePermission requiredRoles={[role.AFFILIATE]}><Affiliate /></RequirePermission>
            </Route>

            <Route path="/advertiser/*" >
              <RequirePermission requiredRoles={[role.ADVERTISER]}><Advertiser /></RequirePermission>
            </Route>

            <Route path="/verify/:token" >
              <Verification />
            </Route>

            <Route path="/admission/*" >
              <RedirectLoggedInUsers><Admission /></RedirectLoggedInUsers>
            </Route>

            <Route path="/logout" ><Logout /></Route>

            <Route path="/" ><Redirect404 /></Route>

          </Switch>

        </Suspense>

      </RootErrorBoundary>
    </Box >
  );
}

export default App;